import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { 
  Users, Plane, MessageCircle, Settings, LogOut, BadgeCheck, 
  MapPin, Briefcase, ChevronLeft, ChevronRight, X, Heart
} from "lucide-react";
import HavnsLogo from "../components/HavnsLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sample profiles for demo (will be replaced with real data)
const sampleProfiles = [
  {
    profile_id: "1",
    name: "Priya",
    age: 29,
    profession: "Architect",
    company: "Studio PKA",
    location: "Mumbai",
    is_verified: true,
    photos: [
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=600&h=800&fit=crop&crop=faces",
      "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=600&h=800&fit=crop",
    ],
    prompts: {
      solo_escape: "Japan during cherry blossom season — I've been planning this trip for 2 years",
      expertise: "Sustainable architecture and finding the best hidden cafes in any city",
      weekend_meetup: "Gallery hopping followed by rooftop conversations"
    }
  },
  {
    profile_id: "2",
    name: "Arjun",
    age: 32,
    profession: "Tech Lead",
    company: "Razorpay",
    location: "Bangalore",
    is_verified: true,
    photos: [
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=800&fit=crop&crop=faces",
      "https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?w=600&h=800&fit=crop&crop=faces",
    ],
    prompts: {
      solo_escape: "Iceland for the northern lights and complete digital detox",
      expertise: "Building products at scale and brewing the perfect pour-over coffee",
      weekend_meetup: "Early morning treks and late night board game sessions"
    }
  },
  {
    profile_id: "3",
    name: "Meera",
    age: 27,
    profession: "Documentary Filmmaker",
    company: "Independent",
    location: "Delhi",
    is_verified: false,
    photos: [
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=600&h=800&fit=crop&crop=faces",
      "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=600&h=800&fit=crop&crop=faces",
    ],
    prompts: {
      solo_escape: "Bhutan — for the monasteries, mountains, and mindfulness",
      expertise: "Storytelling, street food tours, and finding the soul of a city",
      weekend_meetup: "Film screenings, vinyl shopping, and endless conversations over chai"
    }
  },
  {
    profile_id: "4",
    name: "Vikram",
    age: 34,
    profession: "Venture Partner",
    company: "Peak XV",
    location: "Mumbai",
    is_verified: true,
    photos: [
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=600&h=800&fit=crop&crop=faces",
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=600&h=800&fit=crop&crop=faces",
    ],
    prompts: {
      solo_escape: "Patagonia — for the raw wilderness and perspective reset",
      expertise: "Startup ecosystems, jazz, and the art of slow travel",
      weekend_meetup: "Jazz clubs, bookstore browsing, and spontaneous road trips"
    }
  },
];

const Discover = () => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState(sampleProfiles);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const currentProfile = profiles[currentIndex];

  useEffect(() => {
    // For MVP demo, don't require login - show sample profiles
    // In production, check auth here
    fetchProfiles();
  }, [navigate]);

  const fetchProfiles = async () => {
    try {
      const response = await axios.get(`${API}/profiles/discover`);
      if (response.data.length > 0) {
        setProfiles(response.data);
      }
    } catch (error) {
      // Use sample profiles if API fails
      console.log("Using sample profiles");
    }
  };

  const nextPhoto = () => {
    if (currentProfile && currentPhotoIndex < currentProfile.photos.length - 1) {
      setCurrentPhotoIndex(prev => prev + 1);
    }
  };

  const prevPhoto = () => {
    if (currentPhotoIndex > 0) {
      setCurrentPhotoIndex(prev => prev - 1);
    }
  };

  const handlePass = () => {
    setCurrentPhotoIndex(0);
    if (currentIndex < profiles.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      toast.info("You've seen all profiles! Check back later.");
    }
  };

  const handleConnect = (type) => {
    toast.success(`Connection request sent to ${currentProfile.name}!`);
    handlePass();
  };

  const handleLogout = () => {
    localStorage.removeItem("havns_user");
    localStorage.removeItem("havns_profile_id");
    navigate("/");
  };

  if (!currentProfile) {
    return (
      <div className="min-h-screen bg-[#FBF9F7] flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-2xl text-[#1A1A1A] mb-2">No more profiles</h2>
          <p className="text-[#666]">Check back later for new members</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF9F7]">
      {/* Header */}
      <header className="border-b border-[#EEE] bg-[#FBF9F7]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between h-16">
          <HavnsLogo className="text-xl" linkTo="/discover" />
          
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate("/messages")}
              className="p-2 text-[#666] hover:text-[#1A1A1A] transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
            </button>
            <div className="relative">
              <button 
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 text-[#666] hover:text-[#1A1A1A] transition-colors"
              >
                <Settings className="w-5 h-5" />
              </button>
              {showMenu && (
                <div className="absolute right-0 top-12 w-48 bg-white rounded-xl shadow-lg border border-[#EEE] py-2">
                  <button 
                    onClick={() => navigate("/settings")}
                    className="w-full px-4 py-2 text-left text-[#666] hover:bg-[#F5F5F5] flex items-center gap-2"
                  >
                    <BadgeCheck className="w-4 h-4" />
                    Verify Identity
                  </button>
                  <button 
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-red-500 hover:bg-[#F5F5F5] flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {/* Profile Card - Hinge Style */}
        <div className="bg-white rounded-3xl overflow-hidden shadow-xl">
          {/* Photo Section */}
          <div className="relative aspect-[3/4]">
            <img
              src={currentProfile.photos[currentPhotoIndex]}
              alt={currentProfile.name}
              className="w-full h-full object-cover"
            />
            
            {/* Photo Navigation */}
            <div className="absolute top-4 left-4 right-4 flex gap-1">
              {currentProfile.photos.map((_, idx) => (
                <div
                  key={idx}
                  className={`h-1 flex-1 rounded-full ${
                    idx === currentPhotoIndex ? 'bg-white' : 'bg-white/40'
                  }`}
                />
              ))}
            </div>

            {/* Left/Right tap areas */}
            <div 
              className="absolute inset-y-0 left-0 w-1/3 cursor-pointer"
              onClick={prevPhoto}
            />
            <div 
              className="absolute inset-y-0 right-0 w-1/3 cursor-pointer"
              onClick={nextPhoto}
            />

            {/* Name & Basic Info Overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-6 pt-20">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-white text-3xl font-serif">{currentProfile.name}, {currentProfile.age}</h2>
                {currentProfile.is_verified && (
                  <BadgeCheck className="w-6 h-6 text-[#C5B358]" />
                )}
              </div>
              <div className="flex items-center gap-3 text-white/80 text-sm">
                <span className="flex items-center gap-1">
                  <Briefcase className="w-4 h-4" />
                  {currentProfile.profession}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {currentProfile.location}
                </span>
              </div>
            </div>
          </div>

          {/* Prompts Section */}
          <div className="p-6 space-y-4">
            {currentProfile.prompts.solo_escape && (
              <div className="bg-[#FBF9F7] rounded-2xl p-4">
                <p className="text-[#C5B358] text-sm font-medium mb-1">My next solo escape is to...</p>
                <p className="text-[#1A1A1A]">{currentProfile.prompts.solo_escape}</p>
              </div>
            )}
            
            {currentProfile.prompts.expertise && (
              <div className="bg-[#FBF9F7] rounded-2xl p-4">
                <p className="text-[#C5B358] text-sm font-medium mb-1">Ask me about my expertise in...</p>
                <p className="text-[#1A1A1A]">{currentProfile.prompts.expertise}</p>
              </div>
            )}

            {currentProfile.prompts.weekend_meetup && (
              <div className="bg-[#FBF9F7] rounded-2xl p-4">
                <p className="text-[#C5B358] text-sm font-medium mb-1">My ideal weekend meetup involves...</p>
                <p className="text-[#1A1A1A]">{currentProfile.prompts.weekend_meetup}</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="p-6 pt-0 flex gap-3">
            <Button
              onClick={handlePass}
              variant="outline"
              className="flex-1 h-14 rounded-full border-[#EEE] text-[#999] hover:bg-[#F5F5F5]"
            >
              <X className="w-5 h-5 mr-2" />
              Pass
            </Button>
            <Button
              onClick={() => handleConnect('trip')}
              className="flex-1 h-14 rounded-full bg-[#F5F5F5] text-[#1A1A1A] hover:bg-[#EEE]"
            >
              <Plane className="w-5 h-5 mr-2" />
              Trip Buddy
            </Button>
            <Button
              onClick={() => handleConnect('connect')}
              className="flex-1 h-14 rounded-full bg-[#1A1A1A] text-white hover:bg-[#333]"
            >
              <Users className="w-5 h-5 mr-2" />
              Connect
            </Button>
          </div>
        </div>

        {/* Profile Counter */}
        <p className="text-center text-[#999] text-sm mt-6">
          {currentIndex + 1} of {profiles.length} profiles
        </p>
      </main>
    </div>
  );
};

export default Discover;
